import { UserRole, CreatorStatus } from '../enums';
import { CreatorProfile } from '../entities/creator-profile';
import { DomainError } from '../errors/domain-error';

/** Public user slice used in platform identity (no credentials). */
export interface PlatformUser {
  id: string;
  email: string;
  role: UserRole;
  createdAt: Date;
}

/**
 * Unified platform identity: every participant is a User;
 * CreatorProfile is an optional extension, not a separate entity lifecycle.
 */
export interface PlatformIdentity {
  user: PlatformUser;
  creatorProfile: CreatorProfile | null;
}

export function buildPlatformIdentity(
  user: PlatformUser,
  creatorProfile: CreatorProfile | null = null,
): PlatformIdentity {
  return { user, creatorProfile };
}

/** True when the user has no creator extension yet and may onboard. */
export function userCanBecomeCreator(identity: PlatformIdentity): boolean {
  return identity.creatorProfile === null;
}

/** True when the user has an active creator extension. */
export function isActiveCreator(identity: PlatformIdentity): boolean {
  return identity.creatorProfile?.status === CreatorStatus.ACTIVE;
}

/** True when the user has any creator extension (pending, active, or suspended). */
export function hasCreatorExtension(identity: PlatformIdentity): boolean {
  return identity.creatorProfile !== null;
}

/** User evolves into creator — profile attaches to existing user, not a new account. */
export function evolveUserIntoCreator(
  identity: PlatformIdentity,
  creatorProfile: CreatorProfile,
): PlatformIdentity {
  if (creatorProfile.userId !== identity.user.id) {
    throw new DomainError('CreatorProfile must belong to the same User');
  }
  return {
    user: { ...identity.user, role: UserRole.CREATOR },
    creatorProfile,
  };
}
